import React, { ForwardedRef, forwardRef, PropsWithChildren } from 'react';
import AssetWrapper, { AssetWrapperProps } from 'src/app/atoms/Asset/AssetWrapper';
import Clickable from 'src/app/atoms/Clickable/Clickable';
import Stack from 'src/app/atoms/Layout/Stack/Stack';
import Typography, { TypographyProps } from 'src/app/atoms/Typography/Typography';
import { ImageCardContainer, ImageContainer } from './ImageCard.styles';
import Grid from 'src/app/atoms/Layout/Grid/Grid';
import Arrow from 'app/icons/Monospaced/Arrow/Arrow';
import { ColorModePropKeys } from 'styles/colors';

export interface ImageCardProps extends PropsWithChildren {
  href?: string;
  image?: AssetWrapperProps['imageProps'];
  video?: AssetWrapperProps['videoProps'];
  imageMaxWidth?: number;
  title?: TypographyProps;
  supportingText?: TypographyProps;
  subHeading?: TypographyProps;
  className?: string;
  onClick?: () => void;
  imageHeight?: number;
  imageCover?: boolean;
  background?: ColorModePropKeys;
  reversed?: boolean;
}

const ImageCard = (
  {
    href,
    image,
    video,
    title,
    supportingText,
    subHeading,
    className,
    imageMaxWidth,
    onClick,
    imageHeight,
    imageCover,
    background = 'bg-primary',
    reversed = false,
    children,
  }: ImageCardProps,
  ref: ForwardedRef<HTMLDivElement>
): JSX.Element => {
  const hasCardContent = subHeading || title || href || supportingText || children;

  const renderCardContent = (): JSX.Element => (
    <Stack
      gap='spacing-xs'
      direction='column'
      align='flex-start'
      justify='flex-start'
      padding={['spacing-2xl', 'spacing-xl', 'spacing-3xl']}
    >
      {subHeading && (
        <Stack
          align='flex-start'
          justify='flex-start'
          marginBottom='spacing-sm'
        >
          <Typography {...subHeading} />
        </Stack>
      )}
      {(title || href) && (
        <Grid>
          <Stack
            direction='row'
            gap='spacing-xl'
            justify='space-between'
            align='flex-start'
          >
            {title && <Typography {...title} />}
            {href && (
              <Arrow
                color='var(--gray-light-400)'
                className='arrow-icon'
                orientation='upRight'
                width={24}
              />
            )}
          </Stack>
        </Grid>
      )}
      {supportingText && <Typography {...supportingText} />}
      {children}
    </Stack>
  );

  return (
    <ImageCardContainer
      className={className}
      ref={ref}
      $clickable={!!href || !!onClick}
    >
      <Clickable
        href={href}
        onClick={onClick}
      >
        <Stack
          borderRadius='radius-2xl'
          background={background}
          padding='spacing-lg'
          direction={reversed ? 'column-reverse' : 'column'}
          justify={reversed ? 'space-between' : 'flex-start'}
          border='1px solid var(--border-secondary)'
          className='image-card'
        >
          <ImageContainer
            $imageHeight={imageHeight}
            $imageCover={imageCover}
          >
            <AssetWrapper
              imageProps={image}
              videoProps={video}
              maxWidth={imageMaxWidth}
              cover={imageCover}
            />
          </ImageContainer>
          {hasCardContent && renderCardContent()}
        </Stack>
      </Clickable>
    </ImageCardContainer>
  );
};

export default forwardRef(ImageCard);
